import { Box, Text } from '@factorialco/deprecated-design-system'
import { TaskStatusFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import useCreateTaskStatus from 'modules/bookkeepers/hooks/useCreateTaskStatus'
import type {
  IncidenceTaskDocument,
  IncidenceTaskDocumentsQueryData,
} from 'modules/bookkeepers/hooks/useGetIncidenceTaskDocuments'
import useUpdateTaskStatus from 'modules/bookkeepers/hooks/useUpdateTaskStatus'
import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import type { TaskTypes } from 'modules/bookkeepers/store/collections/taskTypes'
import type TaskType from 'modules/bookkeepers/store/models/TaskType'

import IncidenceTaskItem from './_IncidenceTaskItem'

interface Props {
  taskTypes: TaskTypes
  taskDocuments: IncidenceTaskDocumentsQueryData
  incidenceId: string
  trackTaskComplete: (id: string, status: boolean) => void
  refetchTaskDocuments: () => void
}

const IncidenceTasks = ({
  taskTypes,
  taskDocuments,
  incidenceId,
  trackTaskComplete,
  refetchTaskDocuments,
}: Props) => {
  const incidence = useCurrentIncidence()
  const taskStatuses = incidence.taskStatuses
  const { createTaskStatus } = useCreateTaskStatus()
  const { updateTaskStatus } = useUpdateTaskStatus()

  const updateIncidences = (updatedTaskStatus: TaskStatusFragment, taskType: TaskType) => {
    const taskStatus = taskStatuses.find(
      (_taskStatus) => _taskStatus.taskType?.title === taskType.get('title')
    )

    if (taskStatus) {
      taskStatus.completed = updatedTaskStatus.completed
    } else {
      taskStatuses.push(updatedTaskStatus)
    }
  }

  const getTaskType = (taskTypeId: string) => {
    const taskType = taskTypes.find({ id: taskTypeId })

    return taskType
  }

  const getDocument = (taskTypeId: string) => {
    return taskDocuments.find(
      (taskDocument: IncidenceTaskDocument) => taskDocument.taskTypeId === taskTypeId
    )
  }

  const { running, performAction: onSelect } = useAsyncAction(
    async (new_status: boolean, taskTypeId: string, taskStatus?: TaskStatusFragment) => {
      trackTaskComplete(taskTypeId, new_status)
      const taskType = getTaskType(taskTypeId)
      if (!taskType) return

      let updatedTaskStatus

      if (taskStatus) {
        const { data } = await updateTaskStatus(taskStatus.id, new_status)
        updatedTaskStatus = data
      } else {
        const { data } = await createTaskStatus(new_status, taskTypeId, incidenceId)
        updatedTaskStatus = data
      }

      if (!updatedTaskStatus) return

      updateIncidences(updatedTaskStatus, taskType)
      refetchTaskDocuments()
    }
  )

  const onDelete = async () => {
    refetchTaskDocuments()
  }

  const getTaskStatus = (taskType: TaskType) => {
    const taskStatus = taskStatuses.find(
      (status) => status.taskType?.title === taskType.get('title')
    )

    return taskStatus
  }

  if (taskTypes.length === 0) {
    return <></>
  }

  return (
    <Box gap="s24" paddingBottom="s24">
      <Text.Normal weight="semibold" size="300">
        {i18n.t('bookkeepers.incidence.note.tasks.title')}
      </Text.Normal>
      <Box component="ul" gap="s32">
        {taskTypes.map((model) => (
          <IncidenceTaskItem
            incidenceId={incidence.id}
            taskType={model}
            taskStatus={getTaskStatus(model)}
            document={getDocument(model.id)}
            onSelect={onSelect}
            onDelete={onDelete}
            key={model.id}
            disabled={running}
          />
        ))}
      </Box>
    </Box>
  )
}

export default observer(IncidenceTasks)
